import { useEffect, useRef } from 'react';
import { PanelTitle } from '@siliconx/shared/ui/PanelTitle';
import { StatusPill } from '@siliconx/shared/ui/StatusPill';
import { InsightBanner } from '@siliconx/shared/ui/InsightBanner';
import type { BenchPairingTestDevice, BenchPairingTestOptions, BenchPairingTestStage } from '@siliconx/shared';
import {
  anySelectableStage,
  deviceLabel,
  deviceLabelWithIndex,
  groupDevices,
  isSharedKeyLocked,
  removedKey,
  type ProfileGroup,
} from './testSelection';

/** Why a stage cannot be unchecked. Fixed copy; nothing from the server is rendered as an explanation. */
const REASON_COPY: Record<NonNullable<BenchPairingTestStage['notSelectableReason']>, string> = {
  required: 'Required by profile',
  destructive: 'Controlled by wipe authorization',
  not_agent_observed: 'Not run by the bench',
  shared_device_key: 'Shared device identity',
};

export const MEMBER_NOTE = 'Only an org admin can remove tests. Approving runs all tests.';
export const CAP_NOTE =
  'A device with any test removed grades at most Normal wear, never Like new, and so does the machine. Like new means full testing.';

/** A checkbox that can show the mixed state (some devices keep the test, some do not). */
function StageCheckbox({
  id,
  checked,
  mixed,
  disabled,
  describedBy,
  onChange,
}: {
  id: string;
  checked: boolean;
  mixed: boolean;
  disabled: boolean;
  describedBy?: string;
  onChange: (checked: boolean) => void;
}) {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = mixed;
  }, [mixed]);
  return (
    <input
      ref={ref}
      id={id}
      type="checkbox"
      checked={checked}
      disabled={disabled}
      aria-describedby={describedBy}
      onChange={(e) => onChange(e.target.checked)}
    />
  );
}

function DeviceLine({ d }: { d: BenchPairingTestDevice }) {
  // Device text came from the device: rendered as text, never markup.
  return <span className="font-mono text-xs break-all">{deviceLabel(d)}</span>;
}

/**
 * Per-device test selection on the pairing approval screen (SIL-2000). Devices are grouped by the profile they are held to; a
 * group checklist applies to every device in the group and a "Per-device options" list overrides it for one device. Required
 * and wipe stages are checked and locked. A member sees the list read-only; the approve DEFINER function is the real gate,
 * this panel is UX. Controlled: the pair page owns the state (`removed` holds `deviceIndex:stageIndex` keys) so Approve can
 * read it.
 */
export function TestSelectionPanel({
  options,
  removed,
  onSet,
  disabled,
}: {
  options: BenchPairingTestOptions;
  removed: ReadonlySet<string>;
  /** Remove (`remove: true`) or keep a stage on the given devices. */
  onSet: (deviceIndexes: number[], stageIndex: number, remove: boolean) => void;
  disabled: boolean;
}) {
  const { groups, unprofiled } = groupDevices(options);
  const canEdit = options.canDeselect && !disabled;
  const hasSelectable = anySelectableStage(options);

  return (
    <section className="space-y-4 border-t border-border pt-4" aria-label="Tests to run" data-testid="test-selection">
      <PanelTitle title="Tests to run" />

      {!options.canDeselect && (
        <InsightBanner tone="neutral" lead={MEMBER_NOTE} />
      )}
      {options.canDeselect && hasSelectable && <InsightBanner tone="neutral" lead="Removing a test caps the grade">{CAP_NOTE}</InsightBanner>}
      {options.canDeselect && !hasSelectable && (
        <p className="text-sm text-muted-foreground">Every test for this machine is required. Nothing can be removed.</p>
      )}

      {groups.map((g, gi) => (
        <GroupSection key={g.key} group={g} index={gi} removed={removed} canEdit={canEdit} canDeselect={options.canDeselect} onSet={onSet} />
      ))}

      {unprofiled.length > 0 && (
        <section className="space-y-1" aria-label="Devices without tests">
          <h3 className="text-sm font-semibold">No tests for these devices</h3>
          <ul className="space-y-1 text-sm">
            {unprofiled.map((d) => (
              <li key={d.deviceIndex} className="flex flex-wrap items-center gap-2">
                <DeviceLine d={d} />
                <span className="text-muted-foreground">No tests; capped below like-new</span>
              </li>
            ))}
          </ul>
        </section>
      )}
    </section>
  );
}

function GroupSection({
  group,
  index,
  removed,
  canEdit,
  canDeselect,
  onSet,
}: {
  group: ProfileGroup;
  index: number;
  removed: ReadonlySet<string>;
  canEdit: boolean;
  canDeselect: boolean;
  onSet: (deviceIndexes: number[], stageIndex: number, remove: boolean) => void;
}) {
  // Locked by the server for a shared deviceKey (every optional stage reads selectable:false / shared_device_key).
  const editable = group.devices.filter((d) => !isSharedKeyLocked(d));
  const lockedDevices = group.devices.filter(isSharedKeyLocked);
  const lockedNoteId = `sel-g${index}-locked`;
  const hasSelectable = group.stages.some((s) => s.selectable);

  return (
    <section className="space-y-2" aria-label={`Tests for ${group.profile.id}`}>
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="text-sm font-semibold">{group.profile.id}</h3>
        <StatusPill tone="neutral">v{group.profile.version}</StatusPill>
        <span className="text-xs text-muted-foreground">
          {group.devices.length === 1 ? '1 device' : `${group.devices.length} devices`}
        </span>
      </div>
      <ul className="space-y-0.5">
        {group.devices.map((d) => (
          <li key={d.deviceIndex}>
            <DeviceLine d={d} />
          </li>
        ))}
      </ul>

      {group.stages.length === 0 && <p className="text-sm text-muted-foreground">No tests in this profile.</p>}

      <ul className="space-y-1.5 text-sm">
        {group.stages.map((s) => {
          const id = `sel-g${index}-s${s.stageIndex}`;
          const interactive = s.selectable && canEdit && editable.length > 0;
          const removedOn = editable.filter((d) => removed.has(removedKey(d.deviceIndex, s.stageIndex)));
          const keptOn = editable.filter((d) => !removed.has(removedKey(d.deviceIndex, s.stageIndex)));
          // A locked device in the group still needs the stage, so it is served even when every editable device removed it.
          const keptAnywhere = [...keptOn, ...lockedDevices];
          const mixed = s.selectable && removedOn.length > 0 && keptAnywhere.length > 0;
          const checked = !(s.selectable && removedOn.length > 0 && keptOn.length === 0);
          const reasonId = `${id}-reason`;
          const mixedId = `${id}-mixed`;
          const describedBy =
            [
              !s.selectable && s.notSelectableReason ? reasonId : null,
              s.selectable && !canDeselect ? reasonId : null,
              mixed ? mixedId : null,
              lockedDevices.length > 0 && s.selectable ? lockedNoteId : null,
            ]
              .filter((x): x is string => x !== null)
              .join(' ') || undefined;
          return (
            <li key={s.stageIndex} className="space-y-0.5">
              <div className="flex flex-wrap items-center gap-2">
                <StageCheckbox
                  id={id}
                  checked={checked}
                  mixed={mixed}
                  disabled={!interactive}
                  describedBy={describedBy}
                  onChange={(next) => onSet(editable.map((d) => d.deviceIndex), s.stageIndex, !next)}
                />
                <label htmlFor={id} className="font-mono">
                  {s.name}
                </label>
                {!s.selectable && s.notSelectableReason && (
                  <span id={reasonId}>
                    <StatusPill tone="neutral">{REASON_COPY[s.notSelectableReason]}</StatusPill>
                  </span>
                )}
                {s.selectable && !canDeselect && (
                  <span id={reasonId}>
                    <StatusPill tone="neutral">Optional</StatusPill>
                  </span>
                )}
              </div>
              {mixed && (
                <p id={mixedId} className="ml-6 text-xs text-status-amber">
                  Removed on {removedOn.map(deviceLabelWithIndex).join(', ')} but kept on {keptAnywhere.map(deviceLabelWithIndex).join(', ')}. The bench
                  plans tests per profile, so it still runs on all of them. The removal is still recorded and caps the grade.
                </p>
              )}
            </li>
          );
        })}
      </ul>

      {lockedDevices.length > 0 && (
        <p id={lockedNoteId} className="text-xs text-muted-foreground">
          {lockedDevices.map(deviceLabelWithIndex).join(', ')} shares its identity with another device here, so its tests cannot be
          removed.
        </p>
      )}

      {canDeselect && hasSelectable && editable.length > 1 && (
        <details className="text-sm">
          <summary className="cursor-pointer text-muted-foreground">Per-device options</summary>
          <ul className="mt-2 space-y-3">
            {editable.map((d) => (
              <li key={d.deviceIndex}>
                <fieldset className="space-y-1">
                  <legend className="font-mono text-xs break-all">{deviceLabelWithIndex(d)}</legend>
                  <ul className="space-y-1">
                    {d.stages
                      .filter((s) => s.selectable)
                      .map((s) => {
                        const id = `sel-d${d.deviceIndex}-s${s.stageIndex}`;
                        return (
                          <li key={s.stageIndex} className="flex items-center gap-2">
                            <input
                              id={id}
                              type="checkbox"
                              checked={!removed.has(removedKey(d.deviceIndex, s.stageIndex))}
                              disabled={!canEdit}
                              onChange={(e) => onSet([d.deviceIndex], s.stageIndex, !e.target.checked)}
                            />
                            <label htmlFor={id} className="font-mono">
                              {s.name}
                            </label>
                          </li>
                        );
                      })}
                  </ul>
                </fieldset>
              </li>
            ))}
          </ul>
        </details>
      )}
    </section>
  );
}
